import React, { useState } from 'react';
import { Image } from '@vgn-medien-holding/vgn-fe-components';
import { PropsWithClassProps } from '@vgn-medien-holding/vgn-fe-components';
import { twMerge } from 'tailwind-merge';
import { Button } from '@components/atoms/Button/Button';
import { SubscribeToNewsletterCustomerioDocument } from '@src/lib/graphql/generated';
import { Newsletter as NewsletterType } from '@src/lib/graphql/generated';
import { useMutation } from '@lib/graphql/urql';

export interface NewsletterProps extends PropsWithClassProps {
  newsletter?: NewsletterType;
  hasBackground?: boolean;
}

export const Newsletter = ({ newsletter, classProps, hasBackground }: NewsletterProps) => {
  const [form, setForm] = useState({
    firstname: '',
    lastname: '',
    email: '',
  });

  const handleChange = (event, field) => {
    const newForm = Object.assign({}, form);
    newForm[field] = event.target.value;
    setForm(newForm);
  };

  const [, subscribeNewsletter] = useMutation(SubscribeToNewsletterCustomerioDocument);

  if (!newsletter) return null;

  const singleNewsletter = Array.isArray(newsletter) ? newsletter[0] : newsletter;

  const submitForm = ({ firstname, lastname, email }) => {
    subscribeNewsletter({
      newsletter: singleNewsletter.id,
      firstname: firstname,
      lastname: lastname,
      email: email,
      acceptMarketing: true,
    }).then((result) => {
      window.location.href = result?.data?.subscribeToNewsletterCustomerIo?.redirect_url;
    });
  };

  return (
    <div className={twMerge('relative mx-4 overflow-hidden rounded-xl px-5 py-14', 'bg-surface/20', classProps?.root)}>
      {hasBackground && (
        <Image
          src="/assets/tvm_assets/bg-newsletter.png"
          fill
          alt=""
          sizes="100vw"
          copyright=""
          classProps={{ container: 'static', root: 'object-cover' }}
        />
      )}
      <div className="relative mx-auto flex max-w-screen-lg items-center backdrop-blur-sm">
        <div className="flex flex-col gap-x-5 gap-y-10 rounded-lg bg-white/80 p-6 dark:border-0.5 dark:border-surface/60 dark:bg-surface/60 lg:flex-row lg:p-12">
          <div className="flex-1 pr-16 md:basis-1/2">
            <h2
              className={'font-herokid text-3xl font-medium leading-header text-black dark:text-foreground md:text-4xl'}
            >
              E-Mail für dich!
            </h2>
            <p className="pt-2.5 text-base leading-body text-gray-600 dark:text-foreground/60">
              Abonniere unseren Newsletter und erhalte Infos zu Highlights, Neuerscheinungen sowie Filmen und Serien,
              die nur mehr kurz verfügbar sind.
            </p>
          </div>
          <div className="md:basis-1/2">
            <form
              className="flex flex-col"
              onSubmit={($event) => {
                $event.preventDefault();
                submitForm(form);
              }}
            >
              <div className="mb-4 grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <input
                    id="first_name"
                    name="first_name"
                    type="name"
                    autoComplete="given-name"
                    required
                    className="w-full rounded-full border border-gray-500 bg-transparent p-3 text-sm leading-body placeholder:text-black dark:border-foreground/50 dark:placeholder:text-foreground lg:px-2.5"
                    placeholder="Vorname "
                    value={form.firstname}
                    onChange={($event) => {
                      handleChange($event, 'firstname');
                    }}
                  />
                </div>
                <div>
                  <input
                    id="last_name"
                    name="last_name"
                    type="name"
                    autoComplete="family-name"
                    required
                    className="w-full rounded-full border border-gray-500 bg-transparent p-3 text-sm leading-body placeholder:text-black dark:border-foreground/50 dark:placeholder:text-foreground lg:px-2.5"
                    placeholder="Nachname"
                    value={form.lastname}
                    onChange={($event) => {
                      handleChange($event, 'lastname');
                    }}
                  />
                </div>
              </div>

              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="w-full rounded-full border border-gray-500 bg-transparent py-3 text-sm leading-body placeholder:text-black dark:border-foreground/50 dark:placeholder:text-foreground lg:px-2.5"
                placeholder="E-Mail-Adresse"
                value={form.email}
                onChange={($event) => {
                  handleChange($event, 'email');
                }}
              />
              <div className={'pt-6'}>
                <input type="checkbox" id="dsgvo" name="dsgvo" className={'rounded-full bg-transparent'} required />
                <label htmlFor="dsgvo1" className={'ml-1 text-sm'}>
                  Ja, ich möchte die TV-MEDIA-Newsletter regelmäßig per E-Mail erhalten und willige ein, dass meine
                  personenbezogenen Daten (Vor- und Nachname, Anrede, Titel, E-Mail-Adresse und Interaktionen mit
                  Mailings, wie Öffnungs- und Klickverhalten, IP-Adresse und Gerätedaten) durch die TV Media GmbH & Co
                  KG zum Zweck der Zusendung, Erfolgsauswertung und Erstellung allg. Nutzungsstatistiken verarbeitet
                  werden. Die TV-MEDIA-Newsletter enthalten neben redaktionellen Inhalten auch kommerzielle
                  Kommunikation (Werbeanzeigen) Dritter. Die Zusendung kann ich jederzeit widerrufen, z.B. durch den in
                  jedem Newsletter enthaltenen Abmeldelink.
                </label>
              </div>
              <div className={'pt-6'}>
                <input type="checkbox" id="dsgvo" name="dsgvo" className={'rounded-full bg-transparent'} required />
                <label htmlFor="dsgvo2" className={'ml-1 text-sm'}>
                  Ich willige ein, dass meine personenbezogenen Daten (Vor- und Nachname, Anrede, Titel, E-Mail-Adresse,
                  Telefonnummer, Anschrift, Geburtsdatum, Interaktion mit Mailings, wie Öffnungs- und Klickverhalten,
                  Abonnements, Kaufverhalten und Interessen, IP-Adresse und Gerätedaten) durch die VGN Medien Holding
                  GmbH, Gusto GmbH & Co KG, NW Medien GmbH, TOP Media Verlagsservice GmbH, TV Media GmbH & Co KG,
                  Verlagsgruppe News Medienservice GmbH, WoT Business Media GmbH, WoT GmbH & Co KG und WOMAN GmbH & Co
                  KG zu den folgenden Zwecken verarbeitet werden und mich diese per E-Mail, SMS, Telefon und Brief
                  kontaktieren: Erstellung eines individuellen Empfängerprofils, Analyse und Zielgruppensegmentierung,
                  Übermittlung personalisierten Informationsmaterials über eigene Magazine und Online-Medien,
                  personalisierte Marketingmaßnahmen (Gewinnspiele, Veranstaltungen, Newsletter für Waren und
                  Dienstleistungen), Abo- und Sonderwerbeaktionen sowie Erfolgsauswertung und Erstellung allg.
                  Nutzungsstatistiken. Die Einwilligung kann ich jederzeit widerrufen, z.B. per E-Mail an
                  <a href="mailto:datenschutz@vgn.at">
                    <span className={'ml-1 text-black underline dark:text-foreground'}>datenschutz@vgn.at </span>
                  </a>
                  .
                </label>
              </div>
              <div className={'pt-6 text-sm'}>
                Die Rechtmäßigkeit der Datenverarbeitung bleibt bis zum Zeitpunkt eines allfälligen Widerrufs unberührt.
                Die Einwilligung ist freiwillig. Nähere Informationen zum Datenschutz finden Sie in der
                Datenschutz-Policy, z.B. aufrufbar unter{' '}
                <a
                  href="https://vgn.at/Datenschutzpolicy"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  vgn.at/Datenschutzpolicy
                </a>
                .
              </div>
              <Button
                title="Newsletter abonnieren"
                buttonStyle="primary"
                hasAction={true}
                classProps={{ root: '!block !text-primary hover:!text-white text-center mt-6' }}
              />
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
